import React, { useEffect, useState } from "react";
import ExamService from "../../service/exam.service";
import PageNavigation from "../components/PageNavigation";
import { NotificationManager } from "react-notifications";
import { Search, Calculator, Save } from "lucide-react";
import { computeScore } from "../../hooks/answer-evaluation-standalone.js";

const defaultRowResult = () => ({ durum: "", yeniPuan: null, gonderim: "" });

/** İki metni karakter bazlı karşılaştırır; farklı karakterleri vurgulu span ile döner. */
function TextWithDiff({ text1, text2, rowKey, prefix1, prefix2 }) {
    const s1 = String(text1 ?? "");
    const s2 = String(text2 ?? "");
    const maxLen = Math.max(s1.length, s2.length);
    const diffIndices = new Set();
    for (let i = 0; i < maxLen; i++) {
        if (s1[i] !== s2[i]) diffIndices.add(i);
    }
    const renderLine = (str, prefix, lineId) => (
        <span key={lineId}>
            {prefix}
            {str.split("").map((ch, i) =>
                diffIndices.has(i) ? (
                    <span key={`${rowKey}-${lineId}-${i}`} className="text-danger fw-bold" style={{ backgroundColor: "rgba(220,53,69,0.2)" }}>
                        {ch}
                    </span>
                ) : (
                    ch
                )
            )}
        </span>
    );
    return (
        <>
            {renderLine(s1, prefix1, "1")}
            <hr className="my-1" />
            {renderLine(s2, prefix2, "2")}
        </>
    );
}

export default function ChangedAnswer() {
    const [examList, setExamList] = useState([]);
    const [selectedExamId, setSelectedExamId] = useState("");
    const [schools, setSchools] = useState([]);
    const [selectedSchoolId, setSelectedSchoolId] = useState("");
    const [loadingSchools, setLoadingSchools] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [items, setItems] = useState([]);
    const [rowResults, setRowResults] = useState([]);

    useEffect(() => {
        ExamService.getExamsWithAssignments()
            .then((response) => {
                if (response) setExamList(response);
            })
            .catch(() => setExamList([]));
    }, []);

    useEffect(() => {
        if (!selectedExamId) {
            setSchools([]);
            setSelectedSchoolId("");
            return;
        }
        setLoadingSchools(true);
        ExamService.getExamFilterSchools(selectedExamId)
            .then((response) => {
                if (response && Array.isArray(response)) {
                    setSchools(response);
                } else {
                    setSchools([]);
                }
            })
            .catch(() => setSchools([]))
            .finally(() => setLoadingSchools(false));
        setSelectedSchoolId("");
        setItems([]);
    }, [selectedExamId]);

    const handleSearch = (e) => {
        e.preventDefault();
        if (!selectedExamId || !selectedSchoolId) {
            NotificationManager.warning("Lütfen sınav ve okul seçin.", "Uyarı", 3000);
            return;
        }
        setLoading(true);
        setItems([]);
        ExamService.getChangedAnswerOrScore(selectedExamId, selectedSchoolId)
            .then((response) => {
                if (response && Array.isArray(response)) {
                    setItems(response);
                    setRowResults(response.map(defaultRowResult));
                } else {
                    setItems([]);
                    setRowResults([]);
                }
            })
            .catch(() => {
                setItems([]);
                setRowResults([]);
                NotificationManager.error("Veri alınamadı.", "Hata", 3000);
            })
            .finally(() => setLoading(false));
    };

    const handleHesapla = () => {
        if (!items.length) return;
        const newResults = items.map((row) => {
            const maxScore = row.activityPartScore ?? 0;
            const yeniPuan = computeScore(
                row.studentAnswer ?? "",
                row.activityPartAnswer ?? "",
                maxScore
            );
            return { durum: "HESAPLANDI", yeniPuan, gonderim: "" };
        });
        setRowResults(newResults);
        NotificationManager.success(`${items.length} satır için puan hesaplandı.`, "Hesapla", 3000);
    };

    const handleKaydet = async () => {
        const toSave = items
            .map((row, idx) => ({ row, idx }))
            .filter(({ idx }) => rowResults[idx]?.yeniPuan != null);
        if (!toSave.length) {
            NotificationManager.warning("Kaydedilecek hesaplanmış puan yok. Önce Hesapla'ya tıklayın.", "Uyarı", 3000);
            return;
        }
        setSaving(true);
        const updatedIndices = [];
        for (const { row, idx } of toSave) {
            const dto = {
                examResultActivityPartId: row.examResultActivityPartId,
                answer: row.activityPartAnswer ?? "",
                score: row.activityPartScore != null ? row.activityPartScore : 0,
                studentScore: rowResults[idx].yeniPuan,
            };
            const returnedId = await ExamService.updateRecalculationActivityPart(dto);
            if (returnedId != null) {
                const foundIdx = items.findIndex((r) => r.examResultActivityPartId === returnedId);
                if (foundIdx !== -1) updatedIndices.push(foundIdx);
            }
        }
        setRowResults((prev) => {
            const next = [...prev];
            updatedIndices.forEach((i) => {
                next[i] = { ...next[i], gonderim: "KAYDEDİLDİ" };
            });
            return next;
        });
        setSaving(false);
        NotificationManager.success(`${updatedIndices.length} kayıt güncellendi.`, "Kaydedildi", 3000);
    };

    return (
        <React.Fragment>
            <PageNavigation
                main="Componen List"
                sub="Home"
                header="Change Answer"
                description="Sınav ve okula göre cevap/puan değişikliği olan kayıtları listeler"
            />
            <div className="card card-body shadow border-0 table-wrapper table-responsive mt-3">
                <div className="d-flex align-items-center gap-2 mb-3">
                    <Search size={20} className="text-primary" />
                    <span className="text-muted small">
                        Sınav ve okul seçip &quot;Listele&quot; ile sonuçları getirin.
                    </span>
                </div>

                <form onSubmit={handleSearch} className="row g-3 mb-4">
                    <div className="col-12 col-md-6">
                        <label className="form-label">Sınav</label>
                        <select
                            className="form-select"
                            value={selectedExamId}
                            onChange={(e) => setSelectedExamId(e.target.value)}
                        >
                            <option value="">Sınav seçin...</option>
                            {examList.map((exam) => (
                                <option key={exam.id} value={exam.id}>
                                    {exam.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="col-12 col-md-6">
                        <label className="form-label">Okul</label>
                        <select
                            className="form-select"
                            value={selectedSchoolId}
                            onChange={(e) => {
                                setSelectedSchoolId(e.target.value);
                                setItems([]);
                                setRowResults([]);
                            }}
                            disabled={!selectedExamId || loadingSchools}
                        >
                            <option value="">Okul seçin...</option>
                            {schools.map((s, i) => (
                                <option key={`school-${i}-${s.schoolId}`} value={s.schoolId}>
                                    {s.school} (ID: {s.schoolId})
                                </option>
                            ))}
                        </select>
                        {loadingSchools && <span className="small text-muted ms-1">Yükleniyor...</span>}
                    </div>

                    <div className="col-12">
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading || !selectedExamId || !selectedSchoolId}
                        >
                            {loading ? "Yükleniyor..." : "Listele"}
                        </button>
                    </div>
                </form>

                {items.length > 0 && (
                    <div className="table-responsive">
                        <div className="mb-3">
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={handleHesapla}
                            >
                                <Calculator size={18} className="me-1" />
                                Hesapla
                            </button>
                        </div>
                        <table className="table table-hover align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th>Öğrenci No</th>
                                    <th>Öğrenci</th>
                                    <th>Cevap</th>
                                    <th>Öğrenci cevabı</th>
                                    <th>Puan</th>
                                    <th>Öğrenci puanı</th>
                                    <th>Durum</th>
                                    <th>Yeni puan</th>
                                    <th>Gönderim</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((row, idx) => (
                                    <tr key={`${row.examResultActivityPartId}-${idx}`}>
                                        <td>{row.studentId}</td>
                                        <td>{row.studentName} {row.studentLastName}</td>
                                        <td>
                                            <TextWithDiff
                                                text1={row.activityPartAnswer ?? ""}
                                                text2={row.answer ?? ""}
                                                rowKey={`${row.examResultActivityPartId}-${idx}`}
                                                prefix1="Exam: "
                                                prefix2="Result: "
                                            />
                                        </td>
                                        <td>{row.studentAnswer ?? "—"}</td>
                                        <td className={row.activityPartScore !== row.score ? "text-danger fw-bold" : ""}>
                                            Exam: {row.activityPartScore != null ? row.activityPartScore : "—"}
                                            <hr className="my-1" />
                                            Result: {row.score != null ? row.score : "—"}
                                            </td>
                                        <td>{row.studentScore != null ? row.studentScore : "—"}</td>
                                        <td>{rowResults[idx]?.durum ?? ""}</td>
                                        <td>{rowResults[idx]?.yeniPuan != null ? rowResults[idx].yeniPuan : ""}</td>
                                        <td>{rowResults[idx]?.gonderim ?? ""}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="small text-muted mb-2">
                            Toplam {items.length} kayıt.
                        </div>
                        <div className="mt-2">
                            <button
                                type="button"
                                className="btn btn-success"
                                onClick={handleKaydet}
                                disabled={saving || !items.some((_, idx) => rowResults[idx]?.yeniPuan != null)}
                            >
                                <Save size={18} className="me-1" />
                                {saving ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
                            </button>
                        </div>
                    </div>
                )}

                {!loading && items.length === 0 && selectedExamId && selectedSchoolId && (
                    <p className="text-muted mb-0">Bu sınav/okul için değişiklik kaydı bulunamadı.</p>
                )}
            </div>
        </React.Fragment>
    );
}
